import {Component, ElementRef, HostListener, Input, ViewChild, ViewEncapsulation} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CommonConstants, CountryCodes} from '@coreconstant';
import {NgbActiveModal, NgbDate, NgbDateStruct, NgbDatepickerModule} from '@ng-bootstrap/ng-bootstrap';
import {NgSelectModule} from '@ng-select/ng-select';
import {IMaskModule} from 'angular-imask';
import {lastValueFrom} from 'rxjs';
import {EnquireService} from 'src/app/core/services/api-service/enquire.service';
import {noWhitespaceValidator} from 'src/app/core/services/common-service/common.service';
import {ResponseHandlerService} from 'src/app/core/services/common-service/response-handler.service';
import {RecaptchaModule} from 'ng-recaptcha-2';
import {ImagePathConstants, ImageSizeConstants} from '@coreconstant';
import {RecaptchaService} from '../../core/services/api-service/recaptcha.service';

@Component({
    selector: 'app-enquire-venue-vendor',
    imports: [NgbDatepickerModule, ReactiveFormsModule, NgSelectModule, IMaskModule, RecaptchaModule],
    templateUrl: './enquire-venue-vendor.component.html',
    styleUrl: './enquire-venue-vendor.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class EnquireVenueVendorComponent {
    enquireVenueVendorForm: FormGroup;
    @Input() owner_id: any;
    modelList: Array<NgbDateStruct> = [];
    formattedSelectedDates = '';
    isDatepickerOpen: boolean = false;
    countryCodes = CountryCodes
    submitted: boolean = false;
    commonConstants = CommonConstants;
    imagePathConstants = ImagePathConstants;
    imageSizeConstants = ImageSizeConstants;
    @Input() title: any;
    @Input() venueId: any;
    @Input() vendorId: any;
    @Input() venueName: any;
    @Input() ownerName: any;
    @Input() listingLogo: any;
    minDate: any
    @ViewChild('datepickerContainer', {static: false}) datepickerContainer: ElementRef;

    constructor(
        private _formBuilder: FormBuilder,
        private _activeModal: NgbActiveModal,
        private _responseHandlerService: ResponseHandlerService,
        private _enquireService: EnquireService,
        private _recaptchaService: RecaptchaService,
    ) {
    }

    /**
     * Handles the document click event to detect clicks outside a specific target element.
     * If the click occurs outside the `datepickerContainer`, it closes the date picker.
     *
     * @param {Event} event - The click event triggered by the document listener.
     * @return {void} Does not return a value.
     */
    @HostListener('document:click', ['$event'])
    clickOutside(event: Event) {
        if (this.datepickerContainer && !this.datepickerContainer.nativeElement.contains(event.target)) {
            this.isDatepickerOpen = false;
        }
    }

    /**
     * Initializes the component and its dependent processes.
     */
    ngOnInit() {
        this.initializeForm();

        let date = new Date()
        this.minDate = {year: date.getFullYear(), month: (date.getMonth() + 1), day: date.getDate()}
    }

    /**
     * Initializes the form and applies necessary validation rules to each control.
     */
    initializeForm() {
        this.enquireVenueVendorForm = this._formBuilder.group({
            first_name: [null, [Validators.required, noWhitespaceValidator]],
            dates: [null, [Validators.required]],
            mobile: [null, [Validators.required, Validators.pattern(this.commonConstants.MOBILE_REGEX)]],
            email: [null, [Validators.required, Validators.pattern(this.commonConstants.EMAIL_REGEX)]],
            message: [''],
            countryIdForMobile: [this.commonConstants.AUSTRALIA_COUNTRY_CODE, Validators.required],
            flexible: [false],
            recaptcha: ['', Validators.required],
        });
        this.trackFormControlValueChanged();
    }

    /**
     * Handles the resolution of a CAPTCHA, verifying the user's response and updating the form based on the verification result.
     * @param {string} captchaResponse - The CAPTCHA response token generated after user completes the CAPTCHA challenge.
     */
    onCaptchaResolved(captchaResponse: string) {
        let input = {
            token: captchaResponse,
        }
        let verifyCaptchaResponse = this._recaptchaService.verifyCaptcha(input)
        lastValueFrom(verifyCaptchaResponse).then((response: any) => {
            if (response["status"] == true) {
                if (response["data"]["success"] == true) {
                    this.enquireVenueVendorForm.patchValue({recaptcha: response["data"]});
                } else {
                    this.enquireVenueVendorForm.patchValue({recaptcha: null});
                }

            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error);
        });
    }

    /**
     * Tracks the value changes of the 'flexible' form control in the enquireVenueVendorForm.
     * Adjusts the validators of the 'dates' form control based on the value of 'flexible'.
     * If 'flexible' is true, clears the validators and resets the 'dates' control.
     * If 'flexible' is false, sets the required validator on the 'dates' control.
     */
    trackFormControlValueChanged() {
        const datesField = this.enquireVenueVendorForm.get('dates');
        this.enquireVenueVendorForm.get('flexible').valueChanges.subscribe(
            (mode) => {
                if (mode == true) {
                    datesField.clearValidators();
                    datesField.reset();
                } else {
                    datesField.setValidators([Validators.required]);
                }
                datesField.updateValueAndValidity();
            });
    }

    /**
     * Toggles the visibility state of the date picker.
     */
    toggleDatepicker() {
        this.isDatepickerOpen = !this.isDatepickerOpen;
    }

    /**
     * Closes the currently active modal and optionally passes data to it.
     * @param {any} data - Data to be passed to the modal being closed. This parameter is optional.
     */
    closeModal(data) {
        this._activeModal.close(data);
    }

    /**
     * Toggles the selection of a given date.*/
    selectOne(date: NgbDate) {
        const index = this.modelList.findIndex((d: any) => d.equals(date));
        if (index == -1) {
            if (this.modelList?.length < 5) {
                this.modelList.push(date);
            }
        } else {
            this.modelList.splice(index, 1);
        }
        this.updateFormattedDates();
    }

    /**
     * Updates the formatted dates field in the form by converting the model's list of date objects
     */
    private updateFormattedDates() {
        const formattedDates = this.modelList
            .map(d => {
                const date = new Date(d.year, d.month - 1, d.day);
                return date.toLocaleString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'});
            })
            .join(', ');

        this.enquireVenueVendorForm.controls['dates'].setValue(formattedDates);
    }

    /**
     * Determines if the given date is selected by checking against the dates in the current model list.
     * @param {NgbDate} date The date to check against the model list.
     */
    isSelected(date: NgbDate): boolean {
        return this.modelList.some((d: any) => d.equals(date));
    }

    /**
     * Clears the current selection by resetting the model list, formatted selected dates, and the form control value for dates.
     */
    clearSelection() {
        this.modelList = [];
        this.formattedSelectedDates = '';
        this.enquireVenueVendorForm.controls['dates'].setValue('');
    }

    /**
     * Saves the user's date selection and closes the datepicker.
     * Updates the `isDatepickerOpen` property to reflect the closed state.
     */
    saveDateSelection() {
        this.isDatepickerOpen = false;
    }

    /**
     * Submits the form after validating form, prepares the necessary form data, and initiates the API call.
     */
    submit() {
        this.submitted = true;
        if (this.enquireVenueVendorForm.invalid) {
            this.enquireVenueVendorForm.markAllAsTouched();
            this.enquireVenueVendorForm.markAsDirty();
            return
        }

        if (this.title == 'venue-enquire') {
            const flattenedValues = {
                ...this.enquireVenueVendorForm.value,
                ownerId: this.owner_id,
                venue: this.venueId
            }
            let addUpdateRecord = this._enquireService.addVenueEnquire(flattenedValues)
            lastValueFrom(addUpdateRecord).then((response: any) => {
                if (response["status"] == true) {
                    this._responseHandlerService.showSuccessMessageGeneral(response["message"]);
                    this._activeModal.close();
                } else {
                    this._responseHandlerService.showErrorMessageGeneral(response["message"])
                }
            }, (error: any) => {
                this._responseHandlerService.handleAPIErrorResponse(error);
            });
        } else {
            const flattenedValues = {
                ...this.enquireVenueVendorForm.value,
                ownerId: this.owner_id,
                vendor: this.vendorId
            }
            let addUpdateRecord = this._enquireService.addVendorEnquire(flattenedValues)
            lastValueFrom(addUpdateRecord).then((response: any) => {
                if (response["status"] == true) {
                    this._responseHandlerService.showSuccessMessageGeneral(response["message"]);
                    this._activeModal.close();
                } else {
                    this._responseHandlerService.showErrorMessageGeneral(response["message"])
                }
            }, (error: any) => {
                this._responseHandlerService.handleAPIErrorResponse(error);
            });
        }
    }

    /**
     * Sets the default image for an HTMLImageElement when an error occurs
     * @param {Event} event - The event object associated with the image load error.
     */
    setDefaultImage(event: Event) {
        let image = this.commonConstants.DEFAULT_NO_IMAGE;
        (event.target as HTMLImageElement).src = image
    }
}
